// Firebase configuration and messaging
import { initializeApp, getApps } from 'firebase/app';
import { getMessaging, getToken, onMessage } from 'firebase/messaging';

const firebaseConfig = {
  apiKey: process.env.REACT_APP_FIREBASE_API_KEY,
  authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN,
  projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID,
  storageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID,
  appId: process.env.REACT_APP_FIREBASE_APP_ID,
};

// Initialize Firebase
const getFirebaseApp = () => {
  return getApps()[0] || initializeApp(firebaseConfig);
};

// Get messaging instance (with error handling for iOS)
export const getFirebaseMessaging = () => {
  try {
    const app = getFirebaseApp();
    return getMessaging(app);
  } catch (e) {
    console.error('Firebase Messaging not available:', e);
    return null;
  }
};

// DEBUG: Enable this to see alerts in app
const DEBUG_MODE = true;
const debugLog = (msg) => {
  console.log('[Push Debug]', msg);
  if (DEBUG_MODE) {
    window._pushDebugLogs = window._pushDebugLogs || [];
    window._pushDebugLogs.push(msg);
  }
};

// Check if running in native iOS WKWebView (PWAShell/TestFlight)
const isNativeIOSApp = () => {
  try {
    const ua = navigator.userAgent || '';
    const hasPWAShell = ua.includes('PWAShell');
    const hasWebkit = typeof window !== 'undefined' && window.webkit && window.webkit.messageHandlers;
    const hasHandler = hasWebkit && window.webkit.messageHandlers['push-permission-request'];
    
    debugLog(`hasPWAShell: ${hasPWAShell}, hasWebkit: ${!!hasWebkit}, hasHandler: ${!!hasHandler}`);
    
    return hasPWAShell || !!hasHandler;
  } catch (e) {
    debugLog(`isNativeIOSApp error: ${e.message}`);
    return false;
  }
};

// Check if this is iOS Safari (not a PWA, not native app)
const isIOSSafari = () => {
  const ua = navigator.userAgent || '';
  const isIOS = /iPad|iPhone|iPod/.test(ua);
  const isSafari = /Safari/.test(ua) && !/CriOS|FxiOS|EdgiOS/.test(ua);
  const isStandalone = window.navigator.standalone === true;
  const isPWA = window.matchMedia('(display-mode: standalone)').matches;
  
  return isIOS && isSafari && !isStandalone && !isPWA;
};

// Request notification permission via native iOS bridge
const requestNativeIOSPermission = () => {
  return new Promise((resolve) => {
    debugLog('requestNativeIOSPermission called');
    
    let permissionGranted = false;
    let fcmToken = null;
    let resolved = false;
    let retryCount = 0;
    const maxRetries = 5;
    
    const tryResolve = () => {
      if (resolved) return;
      if (permissionGranted && fcmToken) {
        resolved = true;
        cleanup();
        debugLog(`Resolving with FCM token: ${fcmToken.substring(0, 20)}...`);
        resolve(fcmToken);
      }
    };
    
    const cleanup = () => {
      window.removeEventListener('message', handleMessage);
    };
    
    // Retry getting token from native side
    const retryGetToken = () => {
      if (resolved || retryCount >= maxRetries) return;
      retryCount++;
      debugLog(`Retry ${retryCount}/${maxRetries}: Requesting FCM token...`);
      try {
        window.webkit.messageHandlers['push-token'].postMessage({});
      } catch (e) {
        debugLog(`push-token retry failed: ${e.message}`);
      }
    };
    
    const handleMessage = (event) => {
      try {
        // Handle permission result
        if (event.data && event.data.type === 'push-permission-result') {
          debugLog(`Permission granted: ${event.data.granted}`);
          if (event.data.granted) {
            permissionGranted = true;
            tryResolve();
            
            // If no token yet, start retrying after a delay
            if (!fcmToken) {
              debugLog('Permission granted but no token yet, will retry...');
              // Retry every 2 seconds for up to 10 seconds
              setTimeout(retryGetToken, 2000);
              setTimeout(retryGetToken, 4000);
              setTimeout(retryGetToken, 6000);
              setTimeout(retryGetToken, 8000);
              setTimeout(retryGetToken, 10000);
            }
          } else {
            resolved = true;
            cleanup();
            resolve(null);
          }
        }
        
        // Handle FCM token - this might come before or after permission result
        if (event.data && event.data.type === 'fcm-token') {
          debugLog(`FCM token received: ${event.data.token ? event.data.token.substring(0, 20) + '...' : 'null'}`);
          if (event.data.token) {
            fcmToken = event.data.token;
            // If we already have permission, resolve immediately
            if (permissionGranted) {
              tryResolve();
            }
          } else if (event.data.error) {
            debugLog(`FCM token error: ${event.data.error}`);
          }
        }
      } catch (e) {
        debugLog(`handleMessage error: ${e.message}`);
      }
    };
    
    // Set up listener BEFORE calling native code
    window.addEventListener('message', handleMessage);
    
    try {
      debugLog('Calling native push-permission-request');
      window.webkit.messageHandlers['push-permission-request'].postMessage({});
      debugLog('Native call successful');
    } catch (e) {
      debugLog(`Native call failed: ${e.message}`);
      cleanup();
      resolve('ios-native-app');
      return;
    }
    
    // Timeout after 15 seconds (reduced from 30)
    setTimeout(() => {
      if (!resolved) {
        debugLog('Timeout - resolving with fallback');
        cleanup();
        if (fcmToken) {
          resolve(fcmToken);
        } else {
          resolve('ios-native-app');
        }
      }
    }, 15000);
  });
};

// Request FCM token from native iOS
const requestNativeIOSToken = () => {
  return new Promise((resolve) => {
    debugLog('requestNativeIOSToken called');
    
    const handleMessage = (event) => {
      try {
        if (event.data && event.data.type === 'fcm-token') {
          window.removeEventListener('message', handleMessage);
          resolve(event.data.token || 'ios-native-app');
        }
      } catch (e) {
        debugLog(`FCM handleMessage error: ${e.message}`);
      }
    };
    window.addEventListener('message', handleMessage);
    
    try {
      window.webkit.messageHandlers['push-token'].postMessage({});
    } catch (e) {
      debugLog(`push-token call failed: ${e.message}`);
      window.removeEventListener('message', handleMessage);
      resolve('ios-native-app');
    }
    
    setTimeout(() => {
      window.removeEventListener('message', handleMessage);
      resolve('ios-native-app');
    }, 10000);
  });
};

// Get debug logs
export const getDebugLogs = () => window._pushDebugLogs || [];
export const clearDebugLogs = () => { window._pushDebugLogs = []; };

// Check if device is iOS Safari (needs PWA installation)
export const needsPWAInstall = () => isIOSSafari();

// Request notification permission and get FCM token
export const requestNotificationPermission = async () => {
  debugLog('requestNotificationPermission started');
  
  try {
    // Check if this is a native iOS app (TestFlight/App Store)
    if (isNativeIOSApp()) {
      debugLog('Using native iOS bridge');
      const result = await requestNativeIOSPermission();
      debugLog(`Native result: ${result ? 'success' : 'null'}`);
      return result;
    }
    
    // Check if iOS Safari (not PWA) - web push not supported here
    if (isIOSSafari()) {
      debugLog('iOS Safari detected - web push requires PWA installation');
      return 'ios-safari-not-supported';
    }
    
    // Check if Notification API is available
    if (typeof Notification === 'undefined') {
      debugLog('Notification API not available');
      return null;
    }

    // Check current permission status
    if (Notification.permission === 'denied') {
      debugLog('Notifications previously denied');
      return null;
    }

    debugLog('Requesting web notification permission');
    const permission = await Notification.requestPermission();
    debugLog(`Web permission result: ${permission}`);
    
    if (permission !== 'granted') {
      return null;
    }
    
    const messaging = getFirebaseMessaging();
    if (!messaging) {
      debugLog('Firebase Messaging not available');
      return null;
    }
    
    const token = await getToken(messaging, {
      vapidKey: process.env.REACT_APP_FIREBASE_VAPID_KEY,
    });
    
    debugLog(`Web FCM token: ${token ? 'received' : 'null'}`);
    return token;
  } catch (error) {
    debugLog(`Error: ${error.message || error}`);
    console.error('Error getting notification permission:', error);
    return null;
  }
};

// Listen for foreground messages
export const onForegroundMessage = (callback) => {
  const messaging = getFirebaseMessaging();
  return onMessage(messaging, (payload) => {
    console.log('Foreground message received:', payload);
    callback(payload);
  });
};

// Register service worker
export const registerServiceWorker = async () => {
  if ('serviceWorker' in navigator) {
    try {
      const registration = await navigator.serviceWorker.register('/firebase-messaging-sw.js');
      console.log('Service Worker registered:', registration);
      return registration;
    } catch (error) {
      console.error('Service Worker registration failed:', error);
      return null;
    }
  }
  return null;
};

export default getFirebaseApp;
